import graphqlClient from './graphqlClient';
import { normalizeChannel } from '../utils/channelNormalizer';

// Session cache helpers
const getSessionCache = <T>(key: string): T | null => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) return JSON.parse(cached) as T;
  } catch { /* ignore */ }
  return null;
};

const setSessionCache = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch { /* ignore */ }
};

export const fetchAllChannels = async (): Promise<string[]> => {
  try {
    const query = `
      query GetAllChannels {
        udm_poi(
          where: { channel: { _is_null: false, _neq: "" } }
          distinct_on: channel
          order_by: { channel: asc }
        ) {
          channel
        }
      }
    `;

    const response = await graphqlClient.request<{
      udm_poi: Array<{ channel?: string }>;
    }>(query);

    const channels = response.udm_poi
      ?.map(poi => poi.channel)
      .filter((channel): channel is string => Boolean(channel)) || [];

    return channels.map(normalizeChannel);
  } catch (error: any) {
    return [];
  }
};

export const fetchAllRetailers = async (customerType?: 'Customer' | 'Non-Customer' | 'Both'): Promise<string[]> => {
  const cacheKey = `map-cache-retailers-${customerType || 'all'}`;
  const cached = getSessionCache<string[]>(cacheKey);
  if (cached) return cached;

  try {
    let whereClause = `{ ultimate_parent: { _is_null: false, _neq: "" } }`;

    if (customerType === 'Customer') {
      whereClause = `{ _and: [{ ultimate_parent: { _is_null: false, _neq: "" } }, { pois: { customer_id: { _is_null: false } } }] }`;
    } else if (customerType === 'Non-Customer') {
      whereClause = `{ _and: [{ ultimate_parent: { _is_null: false, _neq: "" } }, { pois: { customer_id: { _is_null: true } } }] }`;
    }

    const query = `
      query GetAllRetailers {
        udm_brand(
          where: ${whereClause}
          distinct_on: ultimate_parent
          order_by: { ultimate_parent: asc }
        ) {
          ultimate_parent
        }
      }
    `;

    const response = await graphqlClient.request<{
      udm_brand: Array<{ ultimate_parent?: string }>;
    }>(query);

    const retailers = response.udm_brand
      ?.map(brand => brand.ultimate_parent)
      .filter((parent): parent is string => Boolean(parent)) || [];

    setSessionCache(cacheKey, retailers);
    return retailers;
  } catch (error: any) {
    return [];
  }
};

export interface RetailerWithChannels {
  ultimate_parent: string;
  channels: string[];
}

export const fetchAllRetailersWithChannels = async (): Promise<RetailerWithChannels[]> => {
  const cacheKey = 'map-cache-retailers-with-channels';
  const cached = getSessionCache<RetailerWithChannels[]>(cacheKey);
  if (cached) return cached;

  try {
    const query = `
      query GetAllRetailersWithChannels {
        udm_brand(
          where: { ultimate_parent: { _is_null: false, _neq: "" } }
          order_by: { ultimate_parent: asc }
        ) {
          ultimate_parent
          pois(
            where: { channel: { _is_null: false, _neq: "" } }
            distinct_on: channel
          ) {
            channel
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      udm_brand: Array<{ ultimate_parent?: string; pois?: Array<{ channel?: string }> }>;
    }>(query);

    const retailerMap = new Map<string, Set<string>>();
    for (const brand of response.udm_brand || []) {
      if (!brand.ultimate_parent) continue;
      if (!retailerMap.has(brand.ultimate_parent)) {
        retailerMap.set(brand.ultimate_parent, new Set());
      }
      const channelSet = retailerMap.get(brand.ultimate_parent)!;
      for (const poi of brand.pois || []) {
        if (poi.channel) channelSet.add(normalizeChannel(poi.channel));
      }
    }

    const result: RetailerWithChannels[] = Array.from(retailerMap.entries())
      .map(([ultimate_parent, channels]) => ({
        ultimate_parent,
        channels: Array.from(channels).sort(),
      }))
      .sort((a, b) => a.ultimate_parent.localeCompare(b.ultimate_parent));

    setSessionCache(cacheKey, result);
    return result;
  } catch (error: any) {
    return [];
  }
};

export const fetchBrandsByRetailer = async (ultimateParent: string, customerType?: 'Customer' | 'Non-Customer' | 'Both'): Promise<string[]> => {
  const cacheKey = `map-cache-brands-${ultimateParent}-${customerType || 'all'}`;
  const cached = getSessionCache<string[]>(cacheKey);
  if (cached) return cached;

  try {
    let whereClause = `{ _and: [{ ultimate_parent: { _eq: "${ultimateParent}" } }, { name: { _is_null: false, _neq: "" } }] }`;

    if (customerType === 'Customer') {
      whereClause = `{ _and: [{ ultimate_parent: { _eq: "${ultimateParent}" } }, { name: { _is_null: false, _neq: "" } }, { pois: { customer_id: { _is_null: false } } }] }`;
    } else if (customerType === 'Non-Customer') {
      whereClause = `{ _and: [{ ultimate_parent: { _eq: "${ultimateParent}" } }, { name: { _is_null: false, _neq: "" } }, { pois: { customer_id: { _is_null: true } } }] }`;
    }

    const query = `
      query GetBrandsByRetailer {
        udm_brand(
          where: ${whereClause}
          order_by: { name: asc }
        ) {
          name
        }
      }
    `;

    const response = await graphqlClient.request<{
      udm_brand: Array<{ name?: string }>;
    }>(query);

    const brands = response.udm_brand
      ?.map(brand => brand.name)
      .filter((name): name is string => Boolean(name)) || [];

    setSessionCache(cacheKey, brands);
    return brands;
  } catch (error: any) {
    return [];
  }
};

export interface BrandStoreForMap {
  id: string;
  site: string;
  brand_name: string;
  address?: string | null;
  latitude: number;
  longitude: number;
  channel?: string | null;
  customer_id?: string | null;
  purchek_install_date?: string | null;
  containment_install_date?: string | null;
  cart_manager_install_date?: string | null;
  subscription_start_date?: string | null;
  paired_wheel_install_date?: string | null;
  zipcode?: string | null;
  estimated_no_of_carts_per_store?: number;
  nearby_penetration?: {
    purchek_customer_count?: number;
    penetration_purchek?: number;
    containment_customer_count?: number;
    penetration_containment?: number;
    plaza_cm_customer_count?: number;
    penetration_paired_wheel?: number;
    penetration?: number;
  } | null;
  geo?: {
    city?: string;
    state_code?: string;
    crime_rate?: string;
    total_crime_incidents?: number;
    penalty_for_cart_owner?: boolean;
    no_of_hot_days?: number;
    no_of_cold_days?: number;
  } | null;
  wheel?: {
    single_wheel_replacement_rate?: number;
    service_frequency?: number;
  } | null;
  updated_store_ids?: Array<{ store_id: string; created_at: string }> | null;
}

const calculateBoundingBox = (
  centerLat: number,
  centerLng: number,
  radiusMiles: number
): { minLat: number; maxLat: number; minLng: number; maxLng: number } => {
  const degreesPerMile = 0.0145;
  const latOffset = radiusMiles * degreesPerMile;
  const lngOffset = (radiusMiles * degreesPerMile) / Math.cos((centerLat * Math.PI) / 180);

  return {
    minLat: centerLat - latOffset,
    maxLat: centerLat + latOffset,
    minLng: centerLng - lngOffset,
    maxLng: centerLng + lngOffset
  };
};

const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 3959;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 10) / 10;
};

const calculateCombinedBoundingBox = (
  stores: BrandStoreForMap[],
  radiusMiles: number
): { minLat: number; maxLat: number; minLng: number; maxLng: number } => {
  if (stores.length === 0) {
    return { minLat: 0, maxLat: 0, minLng: 0, maxLng: 0 };
  }

  const latitudes = stores.map(s => s.latitude);
  const longitudes = stores.map(s => s.longitude);

  const minStoreLat = Math.min(...latitudes);
  const maxStoreLat = Math.max(...latitudes);
  const minStoreLng = Math.min(...longitudes);
  const maxStoreLng = Math.max(...longitudes);

  const degreesPerMile = 0.0145;
  const latBuffer = radiusMiles * degreesPerMile;
  const lngBuffer = radiusMiles * degreesPerMile;

  return {
    minLat: minStoreLat - latBuffer,
    maxLat: maxStoreLat + latBuffer,
    minLng: minStoreLng - lngBuffer,
    maxLng: maxStoreLng + lngBuffer
  };
};

export const fetchNeighborhoodStoresForLayer = async (
  stores: BrandStoreForMap[],
  radiusMiles: number = 3,
  customerType?: 'All' | 'Customers' | 'Non-Customers',
  channels?: string[],
  retailers?: string[]
): Promise<BrandStoreForMap[]> => {
  try {
    if (stores.length === 0) {
      return [];
    }

    const bbox = calculateCombinedBoundingBox(stores, radiusMiles);

    const query = `
      query GetLayerNeighborhoodStores(
        $minLat: numeric!
        $maxLat: numeric!
        $minLng: numeric!
        $maxLng: numeric!
      ) @cached {
        udm_poi(
          where: {
            _and: [
              { latitude: { _gte: $minLat } }
              { latitude: { _lte: $maxLat } }
              { longitude: { _gte: $minLng } }
              { longitude: { _lte: $maxLng } }
              { closed_on: { _is_null: true } }
            ]
          }
        ) {
          id
          site
          brand_name
          address
          latitude
          longitude
          channel
          customer_id
          zipcode
          purchek_install_date
          containment_install_date
          cart_manager_install_date
          subscription_start_date
          paired_wheel_install_date
          updated_store_ids(order_by: {created_at: desc}, limit: 1) {
            store_id
            created_at
          }
          nearby_penetration {
            purchek_customer_count
            penetration_purchek
            containment_customer_count
            penetration_containment
            plaza_cm_customer_count
            penetration_paired_wheel
            penetration
          }
          geo {
            city
            state_code
            crime_rate
            total_crime_incidents
            penalty_for_cart_owner
            no_of_hot_days
            no_of_cold_days
          }
          wheel {
            single_wheel_replacement_rate
            service_frequency
          }
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, {
      minLat: bbox.minLat,
      maxLat: bbox.maxLat,
      minLng: bbox.minLng,
      maxLng: bbox.maxLng
    });

    const allNearbyStores = response.udm_poi || [];
    const primaryStoreIds = new Set(stores.map(s => s.id));

    const neighborhoodStores: BrandStoreForMap[] = allNearbyStores
      .filter((store: any) => store.latitude && store.longitude && !primaryStoreIds.has(store.id))
      .map((store: any) => ({
        id: store.id,
        site: store.site,
        brand_name: store.brand_name,
        address: store.address || null,
        latitude: store.latitude,
        longitude: store.longitude,
        channel: store.channel || null,
        customer_id: store.customer_id || null,
        zipcode: store.zipcode || null,
        estimated_no_of_carts_per_store: undefined,
        purchek_install_date: store.purchek_install_date || null,
        containment_install_date: store.containment_install_date || null,
        cart_manager_install_date: store.cart_manager_install_date || null,
        subscription_start_date: store.subscription_start_date || null,
        paired_wheel_install_date: store.paired_wheel_install_date || null,
        nearby_penetration: store.nearby_penetration || null,
        geo: store.geo || null,
        wheel: store.wheel || null,
        updated_store_ids: store.updated_store_ids || null
      }))
      .filter((storeToCheck: BrandStoreForMap) => {
        return stores.some(primaryStore => {
          const distance = calculateDistance(
            primaryStore.latitude,
            primaryStore.longitude,
            storeToCheck.latitude,
            storeToCheck.longitude
          );
          return distance <= radiusMiles;
        });
      });

    const uniqueNeighborhoods = new Map<string, BrandStoreForMap>();
    neighborhoodStores.forEach(store => {
      if (!uniqueNeighborhoods.has(store.id)) {
        uniqueNeighborhoods.set(store.id, store);
      }
    });

    let filteredStores = Array.from(uniqueNeighborhoods.values());

    if (customerType && customerType !== 'All') {
      filteredStores = filteredStores.filter(store => {
        const isCustomer = store.customer_id != null && store.customer_id !== '';

        if (customerType === 'Customers') {
          return isCustomer;
        } else if (customerType === 'Non-Customers') {
          return !isCustomer;
        }
        return true;
      });
    }

    if (channels && channels.length > 0) {
      filteredStores = filteredStores.filter(store =>
        channels.includes(store.channel || '')
      );
    }

    if (retailers && retailers.length > 0) {
      filteredStores = filteredStores.filter(store =>
        retailers.includes(store.brand_name || '')
      );
    }

    return filteredStores;
  } catch (error: any) {
    return [];
  }
};

export const fetchNeighborhoodStores = async (
  latitude: number,
  longitude: number,
  radiusMiles: number = 5,
  excludeStoreId?: string
): Promise<BrandStoreForMap[]> => {
  try {
    const bbox = calculateBoundingBox(latitude, longitude, radiusMiles);

    const query = `
      query GetNeighborhoodStores(
        $minLat: numeric!
        $maxLat: numeric!
        $minLng: numeric!
        $maxLng: numeric!
      ) @cached {
        udm_poi(
          where: {
            _and: [
              { latitude: { _gte: $minLat } }
              { latitude: { _lte: $maxLat } }
              { longitude: { _gte: $minLng } }
              { longitude: { _lte: $maxLng } }
              { closed_on: { _is_null: true } }
            ]
          }
        ) {
          id
          site
          brand_name
          address
          latitude
          longitude
          channel
          customer_id
          zipcode
          purchek_install_date
          containment_install_date
          cart_manager_install_date
          subscription_start_date
          paired_wheel_install_date
          updated_store_ids(order_by: {created_at: desc}, limit: 1) {
            store_id
            created_at
          }
          nearby_penetration {
            purchek_customer_count
            penetration_purchek
            containment_customer_count
            penetration_containment
            plaza_cm_customer_count
            penetration_paired_wheel
            penetration
          }
          geo {
            city
            state_code
            crime_rate
            total_crime_incidents
            penalty_for_cart_owner
            no_of_hot_days
            no_of_cold_days
          }
          wheel {
            single_wheel_replacement_rate
            service_frequency
          }
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, {
      minLat: bbox.minLat,
      maxLat: bbox.maxLat,
      minLng: bbox.minLng,
      maxLng: bbox.maxLng
    });

    const stores = response.udm_poi || [];

    const neighborhoodStores: BrandStoreForMap[] = stores
      .filter((store: any) => store.latitude && store.longitude && store.id !== excludeStoreId)
      .map((store: any) => ({
        id: store.id,
        site: store.site,
        brand_name: store.brand_name,
        address: store.address || null,
        latitude: store.latitude,
        longitude: store.longitude,
        channel: store.channel || null,
        customer_id: store.customer_id || null,
        zipcode: store.zipcode || null,
        estimated_no_of_carts_per_store: undefined,
        purchek_install_date: store.purchek_install_date || null,
        containment_install_date: store.containment_install_date || null,
        cart_manager_install_date: store.cart_manager_install_date || null,
        subscription_start_date: store.subscription_start_date || null,
        paired_wheel_install_date: store.paired_wheel_install_date || null,
        nearby_penetration: store.nearby_penetration || null,
        geo: store.geo || null,
        wheel: store.wheel || null,
        updated_store_ids: store.updated_store_ids || null
      }))
      .filter((store: BrandStoreForMap) => {
        const distance = calculateDistance(latitude, longitude, store.latitude, store.longitude);
        return distance <= radiusMiles;
      })
      .sort((a: BrandStoreForMap, b: BrandStoreForMap) => {
        const distA = calculateDistance(latitude, longitude, a.latitude, a.longitude);
        const distB = calculateDistance(latitude, longitude, b.latitude, b.longitude);
        return distA - distB;
      });

    return neighborhoodStores;
  } catch (error: any) {
    return [];
  }
};

export interface BrandStoreFilters {
  customerType?: 'Customer' | 'Non-Customer' | 'Both';
  states?: string[];
  cities?: string[];
  penetrationMin?: number;
  penetrationMax?: number;
  viewingMode?: 'system-recommended' | 'all-white-space' | 'all-stores';
  whitespaceOpportunities?: string[];
  crimeIndex?: string[];
  weatherHotMonthsMin?: number;
  weatherHotMonthsMax?: number;
  weatherColdMonthsMin?: number;
  weatherColdMonthsMax?: number;
  shoppingComplexOnly?: boolean;
  cartOrdinance?: string;
  // Recommendation thresholds for system-recommended mode
  recommendationThresholds?: {
    purchekNeighborhood?: number;
    purchekPenetration?: number;
    purchekCrime?: number;
    containmentNeighborhood?: number;
    containmentPenetration?: number;
    containmentCrime?: number;
    containmentPlaza?: number;
    containmentOrdinance?: number;
    cartManagerHotDays?: number;
    cartManagerColdDays?: number;
  };
}

export const fetchBrandStoresForMap = async (
  brandNames: string[],
  filters: BrandStoreFilters = {}
): Promise<BrandStoreForMap[]> => {
  try {
    if (brandNames.length === 0) return [];

    const {
      customerType, states, cities,
      penetrationMin, penetrationMax,
      viewingMode, whitespaceOpportunities,
      crimeIndex,
      weatherHotMonthsMin, weatherHotMonthsMax,
      weatherColdMonthsMin, weatherColdMonthsMax,
      shoppingComplexOnly, cartOrdinance,
      recommendationThresholds
    } = filters;

    let andConditions = [
      '{ latitude: { _is_null: false } }',
      '{ longitude: { _is_null: false } }',
      '{ closed_on: { _is_null: true } }'
    ];

    if (customerType === 'Customer') {
      andConditions.push('{ customer_id: { _is_null: false } }');
    } else if (customerType === 'Non-Customer') {
      andConditions.push('{ customer_id: { _is_null: true } }');
    }

    if (states && states.length > 0) {
      const stateFilter = states.map(s => `"${s.toLowerCase()}"`).join(', ');
      andConditions.push(`{ geo: { state: { _in: [${stateFilter}] } } }`);
    }

    if (cities && cities.length > 0) {
      const cityFilter = cities.map(c => `"${c.toLowerCase()}"`).join(', ');
      andConditions.push(`{ geo: { city: { _in: [${cityFilter}] } } }`);
    }

    // Penetration filter (DB stores as whole percentage 0-100, UI slider also 0-100)
    if (penetrationMin !== undefined && penetrationMin > 0) {
      andConditions.push(`{ nearby_penetration: { penetration: { _gte: ${penetrationMin} } } }`);
    }
    if (penetrationMax !== undefined && penetrationMax < 100) {
      andConditions.push(`{ nearby_penetration: { penetration: { _lte: ${penetrationMax} } } }`);
    }

    // Crime index filter (total_crime_incidents thresholds: low <1000, medium 1000-3000, high 3000-6000, critical >6000)
    if (crimeIndex && crimeIndex.length > 0 && crimeIndex.length < 4) {
      const crimeOrConditions: string[] = [];
      if (crimeIndex.includes('low')) {
        crimeOrConditions.push('{ geo: { total_crime_incidents: { _lt: 1000 } } }');
      }
      if (crimeIndex.includes('medium')) {
        crimeOrConditions.push('{ _and: [{ geo: { total_crime_incidents: { _gte: 1000 } } }, { geo: { total_crime_incidents: { _lt: 3000 } } }] }');
      }
      if (crimeIndex.includes('high')) {
        crimeOrConditions.push('{ _and: [{ geo: { total_crime_incidents: { _gte: 3000 } } }, { geo: { total_crime_incidents: { _lt: 6000 } } }] }');
      }
      if (crimeIndex.includes('critical')) {
        crimeOrConditions.push('{ geo: { total_crime_incidents: { _gte: 6000 } } }');
      }
      if (crimeOrConditions.length > 0) {
        andConditions.push(`{ _or: [${crimeOrConditions.join(', ')}] }`);
      }
    }

    // Weather filters (UI shows months, DB stores days. 1 month ≈ 30 days)
    if (weatherHotMonthsMin !== undefined && weatherHotMonthsMin > 0) {
      andConditions.push(`{ geo: { no_of_hot_days: { _gte: ${weatherHotMonthsMin * 30} } } }`);
    }
    if (weatherHotMonthsMax !== undefined && weatherHotMonthsMax < 12) {
      andConditions.push(`{ geo: { no_of_hot_days: { _lte: ${weatherHotMonthsMax * 30} } } }`);
    }
    if (weatherColdMonthsMin !== undefined && weatherColdMonthsMin > 0) {
      andConditions.push(`{ geo: { no_of_cold_days: { _gte: ${weatherColdMonthsMin * 30} } } }`);
    }
    if (weatherColdMonthsMax !== undefined && weatherColdMonthsMax < 12) {
      andConditions.push(`{ geo: { no_of_cold_days: { _lte: ${weatherColdMonthsMax * 30} } } }`);
    }

    // Shopping complex filter (plaza_cm_customer_count > 0 means it's in a shopping complex)
    if (shoppingComplexOnly) {
      andConditions.push('{ nearby_penetration: { plaza_cm_customer_count: { _gt: 0 } } }');
    }

    // Cart ordinance filter
    if (cartOrdinance && cartOrdinance !== 'Any') {
      if (cartOrdinance === 'Yes') {
        andConditions.push('{ geo: { penalty_for_cart_owner: { _eq: true } } }');
      } else if (cartOrdinance === 'No') {
        andConditions.push('{ _or: [{ geo: { penalty_for_cart_owner: { _eq: false } } }, { geo: { penalty_for_cart_owner: { _is_null: true } } }] }');
      }
    }

    // Whitespace filter — show stores missing install dates for selected products (OR logic)
    if (viewingMode === 'all-white-space' && whitespaceOpportunities && whitespaceOpportunities.length > 0) {
      const wsOrConditions: string[] = [];
      if (whitespaceOpportunities.includes('purchek')) {
        wsOrConditions.push('{ purchek_install_date: { _is_null: true } }');
      }
      if (whitespaceOpportunities.includes('cart-containment')) {
        wsOrConditions.push('{ containment_install_date: { _is_null: true } }');
      }
      if (whitespaceOpportunities.includes('cart-manager')) {
        wsOrConditions.push('{ cart_manager_install_date: { _is_null: true } }');
      }
      if (whitespaceOpportunities.includes('subscription')) {
        wsOrConditions.push('{ subscription_start_date: { _is_null: true } }');
      }
      if (whitespaceOpportunities.includes('paired-wheels')) {
        wsOrConditions.push('{ paired_wheel_install_date: { _is_null: true } }');
      }
      if (wsOrConditions.length > 0) {
        andConditions.push(`{ _or: [${wsOrConditions.join(', ')}] }`);
      }
    }

    // System Recommended filter — stores that match recommendation thresholds (OR across product types)
    if (viewingMode === 'system-recommended' && recommendationThresholds) {
      const recOrConditions: string[] = [];
      const t = recommendationThresholds;

      // Purchek recommendation: no purchek + (neighborhood OR penetration OR crime)
      const shouldCheckPurchek = !whitespaceOpportunities || whitespaceOpportunities.length === 0 || whitespaceOpportunities.includes('purchek');
      if (shouldCheckPurchek) {
        const purchekConditions: string[] = [];
        if (t.purchekNeighborhood) purchekConditions.push(`{ nearby_penetration: { purchek_customer_count: { _gte: ${t.purchekNeighborhood} } } }`);
        if (t.purchekPenetration) purchekConditions.push(`{ nearby_penetration: { penetration_purchek: { _gte: ${t.purchekPenetration} } } }`);
        if (t.purchekCrime) purchekConditions.push(`{ geo: { total_crime_incidents: { _gte: ${t.purchekCrime} } } }`);
        if (purchekConditions.length > 0) {
          recOrConditions.push(`{ _and: [{ purchek_install_date: { _is_null: true } }, { _or: [${purchekConditions.join(', ')}] }] }`);
        }
      }

      // Containment recommendation: no containment + (neighborhood OR penetration OR crime OR plaza OR ordinance)
      const shouldCheckContainment = !whitespaceOpportunities || whitespaceOpportunities.length === 0 || whitespaceOpportunities.includes('cart-containment');
      if (shouldCheckContainment) {
        const containmentConditions: string[] = [];
        if (t.containmentNeighborhood) containmentConditions.push(`{ nearby_penetration: { containment_customer_count: { _gte: ${t.containmentNeighborhood} } } }`);
        if (t.containmentPenetration) containmentConditions.push(`{ nearby_penetration: { penetration_containment: { _gte: ${t.containmentPenetration} } } }`);
        if (t.containmentCrime) containmentConditions.push(`{ geo: { total_crime_incidents: { _gte: ${t.containmentCrime} } } }`);
        if (t.containmentPlaza && t.containmentPlaza > 0) containmentConditions.push('{ nearby_penetration: { plaza_cm_customer_count: { _gt: 0 } } }');
        if (t.containmentOrdinance && t.containmentOrdinance > 0) containmentConditions.push('{ geo: { penalty_for_cart_owner: { _eq: true } } }');
        if (containmentConditions.length > 0) {
          recOrConditions.push(`{ _and: [{ containment_install_date: { _is_null: true } }, { _or: [${containmentConditions.join(', ')}] }] }`);
        }
      }

      // Cart Manager recommendation: no cart_manager + (hot days OR cold days)
      const shouldCheckCartManager = !whitespaceOpportunities || whitespaceOpportunities.length === 0 || whitespaceOpportunities.includes('cart-manager');
      if (shouldCheckCartManager) {
        const cmConditions: string[] = [];
        if (t.cartManagerHotDays) cmConditions.push(`{ geo: { no_of_hot_days: { _gte: ${t.cartManagerHotDays} } } }`);
        if (t.cartManagerColdDays) cmConditions.push(`{ geo: { no_of_cold_days: { _gte: ${t.cartManagerColdDays} } } }`);
        if (cmConditions.length > 0) {
          recOrConditions.push(`{ _and: [{ cart_manager_install_date: { _is_null: true } }, { _or: [${cmConditions.join(', ')}] }] }`);
        }
      }

      // Subscription recommendation: no subscription + has purchek
      const shouldCheckSubscription = !whitespaceOpportunities || whitespaceOpportunities.length === 0 || whitespaceOpportunities.includes('subscription');
      if (shouldCheckSubscription) {
        recOrConditions.push('{ _and: [{ subscription_start_date: { _is_null: true } }, { purchek_install_date: { _is_null: false } }] }');
      }

      // Paired Wheels recommendation: no paired wheels + (penetration OR replacement rate OR service frequency)
      const shouldCheckPairedWheels = !whitespaceOpportunities || whitespaceOpportunities.length === 0 || whitespaceOpportunities.includes('paired-wheels');
      if (shouldCheckPairedWheels) {
        recOrConditions.push('{ _and: [{ paired_wheel_install_date: { _is_null: true } }, { _or: [{ nearby_penetration: { penetration_paired_wheel: { _gte: 75 } } }, { wheel: { single_wheel_replacement_rate: { _gte: 4 } } }, { wheel: { service_frequency: { _gte: 50 } } }] }] }');
      }

      if (recOrConditions.length > 0) {
        andConditions.push(`{ _or: [${recOrConditions.join(', ')}] }`);
      }
    }

    const andClause = andConditions.join('\n                ');

    const query = `
      query GetBrandStoresForMap($brandNames: [String!]!) @cached {
        udm_brand(where: { name: { _in: $brandNames } }) {
          name
          estimated_no_of_carts_per_store
          pois(
            where: {
              _and: [
                ${andClause}
              ]
            }
            order_by: [{ site: asc }]
          ) {
            id
            site
            brand_name
            address
            latitude
            longitude
            channel
            customer_id
            zipcode
            purchek_install_date
            containment_install_date
            cart_manager_install_date
            subscription_start_date
            paired_wheel_install_date
            updated_store_ids(order_by: {created_at: desc}, limit: 1) {
              store_id
              created_at
            }
            nearby_penetration {
              purchek_customer_count
              penetration_purchek
              containment_customer_count
              penetration_containment
              plaza_cm_customer_count
              penetration_paired_wheel
              penetration
            }
            geo {
              city
              state_code
              crime_rate
              total_crime_incidents
              penalty_for_cart_owner
              no_of_hot_days
              no_of_cold_days
            }
            wheel {
              single_wheel_replacement_rate
              service_frequency
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      udm_brand: Array<{
        name?: string;
        estimated_no_of_carts_per_store?: number;
        pois?: Array<any>;
      }>;
    }>(query, { brandNames });

    const stores: BrandStoreForMap[] = [];
    for (const brand of (response.udm_brand || [])) {
      const estimatedCarts = brand.estimated_no_of_carts_per_store || undefined;
      for (const poi of (brand.pois || [])) {
        stores.push({
          id: poi.id,
          site: poi.site || 'Unknown Store',
          brand_name: poi.brand_name || 'Unknown Brand',
          address: poi.address || null,
          latitude: poi.latitude,
          longitude: poi.longitude,
          channel: poi.channel || null,
          customer_id: poi.customer_id || null,
          zipcode: poi.zipcode || null,
          estimated_no_of_carts_per_store: estimatedCarts,
          purchek_install_date: poi.purchek_install_date || null,
          containment_install_date: poi.containment_install_date || null,
          cart_manager_install_date: poi.cart_manager_install_date || null,
          subscription_start_date: poi.subscription_start_date || null,
          paired_wheel_install_date: poi.paired_wheel_install_date || null,
          nearby_penetration: poi.nearby_penetration || null,
          geo: poi.geo || null,
          wheel: poi.wheel || null,
          updated_store_ids: poi.updated_store_ids || null
        });
      }
    }

    return stores;
  } catch (error: any) {
    return [];
  }
};

export interface StateStoreCount {
  state_code: string;
  count: number;
  customers: number;
  centroid_lat?: number;
  centroid_lng?: number;
}

export interface ViewportBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

const POI_FIELDS_LIGHT = `
  id site brand_name address latitude longitude channel customer_id zipcode
  purchek_install_date containment_install_date cart_manager_install_date
  subscription_start_date paired_wheel_install_date
  updated_store_ids(order_by: {created_at: desc}, limit: 1) {
    store_id
    created_at
  }
  nearby_penetration {
    purchek_customer_count penetration_purchek
    containment_customer_count penetration_containment
    plaza_cm_customer_count penetration_paired_wheel penetration
  }
  geo {
    city state_code crime_rate total_crime_incidents
    penalty_for_cart_owner no_of_hot_days no_of_cold_days
  }
  wheel { single_wheel_replacement_rate service_frequency }
`;

const POI_FIELDS = POI_FIELDS_LIGHT;

const mapPoiToStore = (poi: any): BrandStoreForMap => ({
  id: poi.id,
  site: poi.site,
  brand_name: poi.brand_name,
  address: poi.address || null,
  latitude: poi.latitude,
  longitude: poi.longitude,
  channel: poi.channel || null,
  customer_id: poi.customer_id || null,
  zipcode: poi.zipcode || null,
  estimated_no_of_carts_per_store: undefined,
  purchek_install_date: poi.purchek_install_date || null,
  containment_install_date: poi.containment_install_date || null,
  cart_manager_install_date: poi.cart_manager_install_date || null,
  subscription_start_date: poi.subscription_start_date || null,
  paired_wheel_install_date: poi.paired_wheel_install_date || null,
  nearby_penetration: poi.nearby_penetration || null,
  geo: poi.geo || null,
  wheel: poi.wheel || null,
  updated_store_ids: poi.updated_store_ids || null,
});

// Build viewing mode condition for queries (returns raw _or condition without leading comma)
const getViewingModeOrCondition = (viewingMode?: string): string => {
  if (viewingMode === 'whitespace') {
    return `_or: [{ purchek_install_date: { _is_null: true } }, { containment_install_date: { _is_null: true } }, { cart_manager_install_date: { _is_null: true } }, { subscription_start_date: { _is_null: true } }, { paired_wheel_install_date: { _is_null: true } }]`;
  }
  if (viewingMode === 'recommended') {
    return `_or: [
      { _and: [{ purchek_install_date: { _is_null: true } }, { _or: [{ nearby_penetration: { purchek_customer_count: { _gte: 5 } } }, { nearby_penetration: { penetration_purchek: { _gte: 75 } } }, { geo: { total_crime_incidents: { _gte: 5000 } } }] }] },
      { _and: [{ containment_install_date: { _is_null: true } }, { _or: [{ nearby_penetration: { containment_customer_count: { _gte: 5 } } }, { nearby_penetration: { penetration_containment: { _gte: 75 } } }, { geo: { total_crime_incidents: { _gte: 5000 } } }, { geo: { penalty_for_cart_owner: { _eq: true } } }, { nearby_penetration: { plaza_cm_customer_count: { _gt: 0 } } }] }] },
      { _and: [{ cart_manager_install_date: { _is_null: true } }, { _or: [{ geo: { no_of_cold_days: { _gte: 90 } } }, { geo: { no_of_hot_days: { _gte: 90 } } }] }] }
    ]`;
  }
  return '';
};

export interface AdvancedFilters {
  penetrationMin?: number;
  penetrationMax?: number;
  crimeIndex?: string[];
  weatherHotMonthsMin?: number;
  weatherHotMonthsMax?: number;
  weatherColdMonthsMin?: number;
  weatherColdMonthsMax?: number;
  shoppingComplexOnly?: boolean;
  cartOrdinance?: string;
  solutions?: string[];
  whitespace?: string[];
}

const buildAdvancedConditions = (filters?: AdvancedFilters): string[] => {
  const conditions: string[] = [];
  if (!filters) return conditions;

  if (filters.penetrationMin !== undefined && filters.penetrationMin > 0) {
    conditions.push(`nearby_penetration: { penetration: { _gte: ${filters.penetrationMin} } }`);
  }
  if (filters.penetrationMax !== undefined && filters.penetrationMax < 100) {
    conditions.push(`nearby_penetration: { penetration: { _lte: ${filters.penetrationMax} } }`);
  }

  if (filters.crimeIndex && filters.crimeIndex.length > 0 && filters.crimeIndex.length < 4) {
    const crimeOrConditions: string[] = [];
    if (filters.crimeIndex.includes('low')) crimeOrConditions.push('{ geo: { total_crime_incidents: { _lt: 1000 } } }');
    if (filters.crimeIndex.includes('medium')) crimeOrConditions.push('{ _and: [{ geo: { total_crime_incidents: { _gte: 1000 } } }, { geo: { total_crime_incidents: { _lt: 3000 } } }] }');
    if (filters.crimeIndex.includes('high')) crimeOrConditions.push('{ _and: [{ geo: { total_crime_incidents: { _gte: 3000 } } }, { geo: { total_crime_incidents: { _lt: 6000 } } }] }');
    if (filters.crimeIndex.includes('critical')) crimeOrConditions.push('{ geo: { total_crime_incidents: { _gte: 6000 } } }');
    if (crimeOrConditions.length > 0) {
      conditions.push(`_or: [${crimeOrConditions.join(', ')}]`);
    }
  }

  // Convert months to days (UI shows months, DB stores days). 1 month ≈ 30 days.
  if (filters.weatherHotMonthsMin !== undefined && filters.weatherHotMonthsMin > 0) {
    conditions.push(`geo: { no_of_hot_days: { _gte: ${filters.weatherHotMonthsMin * 30} } }`);
  }
  if (filters.weatherHotMonthsMax !== undefined && filters.weatherHotMonthsMax < 12) {
    conditions.push(`geo: { no_of_hot_days: { _lte: ${filters.weatherHotMonthsMax * 30} } }`);
  }
  if (filters.weatherColdMonthsMin !== undefined && filters.weatherColdMonthsMin > 0) {
    conditions.push(`geo: { no_of_cold_days: { _gte: ${filters.weatherColdMonthsMin * 30} } }`);
  }
  if (filters.weatherColdMonthsMax !== undefined && filters.weatherColdMonthsMax < 12) {
    conditions.push(`geo: { no_of_cold_days: { _lte: ${filters.weatherColdMonthsMax * 30} } }`);
  }

  // Installed solutions: store must have the install date (not null)
  if (filters.solutions && filters.solutions.length > 0) {
    const solutionFieldMap: Record<string, string> = {
      'Purchek': 'purchek_install_date',
      'Cart Containment': 'containment_install_date',
      'Cart Manager': 'cart_manager_install_date',
      'Subscription': 'subscription_start_date',
      'Paired Wheel': 'paired_wheel_install_date',
    };
    filters.solutions.forEach(sol => {
      const field = solutionFieldMap[sol];
      if (field) {
        conditions.push(`${field}: { _is_null: false }`);
      }
    });
  }

  // Whitespace solutions: store must NOT have the install date (is null)
  if (filters.whitespace && filters.whitespace.length > 0) {
    const solutionFieldMap: Record<string, string> = {
      'Purchek': 'purchek_install_date',
      'Cart Containment': 'containment_install_date',
      'Cart Manager': 'cart_manager_install_date',
      'Subscription': 'subscription_start_date',
      'Paired Wheel': 'paired_wheel_install_date',
    };
    filters.whitespace.forEach(sol => {
      const field = solutionFieldMap[sol];
      if (field) {
        conditions.push(`${field}: { _is_null: true }`);
      }
    });
  }

  if (filters.shoppingComplexOnly) {
    conditions.push(`nearby_penetration: { plaza_cm_customer_count: { _gt: 0 } }`);
  }

  if (filters.cartOrdinance && filters.cartOrdinance !== 'Any') {
    if (filters.cartOrdinance === 'Yes') {
      conditions.push(`geo: { penalty_for_cart_owner: { _eq: true } }`);
    } else if (filters.cartOrdinance === 'No') {
      conditions.push(`_or: [{ geo: { penalty_for_cart_owner: { _eq: false } } }, { geo: { penalty_for_cart_owner: { _is_null: true } } }]`);
    }
  }

  return conditions;
};

// Build inline geo-level conditions (placed inside the geo: {} block in fetchStoreCountsByState)
const buildGeoConditionsInline = (filters?: AdvancedFilters): string => {
  const parts: string[] = [];
  if (!filters) return '';

  // Weather: convert months to days (UI shows months, DB stores days). 1 month ≈ 30 days.
  if (filters.weatherHotMonthsMin !== undefined && filters.weatherHotMonthsMin > 0) {
    parts.push(`no_of_hot_days: { _gte: ${filters.weatherHotMonthsMin * 30} }`);
  }
  if (filters.weatherHotMonthsMax !== undefined && filters.weatherHotMonthsMax < 12) {
    parts.push(`no_of_hot_days: { _lte: ${filters.weatherHotMonthsMax * 30} }`);
  }
  if (filters.weatherColdMonthsMin !== undefined && filters.weatherColdMonthsMin > 0) {
    parts.push(`no_of_cold_days: { _gte: ${filters.weatherColdMonthsMin * 30} }`);
  }
  if (filters.weatherColdMonthsMax !== undefined && filters.weatherColdMonthsMax < 12) {
    parts.push(`no_of_cold_days: { _lte: ${filters.weatherColdMonthsMax * 30} }`);
  }

  // Cart ordinance
  if (filters.cartOrdinance && filters.cartOrdinance !== 'Any') {
    if (filters.cartOrdinance === 'Yes') {
      parts.push(`penalty_for_cart_owner: { _eq: true }`);
    }
    // "No" handled separately via _and since it needs _or with _is_null
  }

  return parts.length > 0 ? `, ${parts.join(', ')}` : '';
};

// Build non-geo _and conditions for inline where clauses (fetchStoreCountsByState)
const buildNonGeoConditionsInline = (filters?: AdvancedFilters): string => {
  const conditions: string[] = [];
  if (!filters) return '';

  if (filters.penetrationMin !== undefined && filters.penetrationMin > 0) {
    conditions.push(`{ nearby_penetration: { penetration: { _gte: ${filters.penetrationMin} } } }`);
  }
  if (filters.penetrationMax !== undefined && filters.penetrationMax < 100) {
    conditions.push(`{ nearby_penetration: { penetration: { _lte: ${filters.penetrationMax} } } }`);
  }

  if (filters.crimeIndex && filters.crimeIndex.length > 0 && filters.crimeIndex.length < 4) {
    const crimeOrConditions: string[] = [];
    if (filters.crimeIndex.includes('low')) crimeOrConditions.push('{ geo: { total_crime_incidents: { _lt: 1000 } } }');
    if (filters.crimeIndex.includes('medium')) crimeOrConditions.push('{ _and: [{ geo: { total_crime_incidents: { _gte: 1000 } } }, { geo: { total_crime_incidents: { _lt: 3000 } } }] }');
    if (filters.crimeIndex.includes('high')) crimeOrConditions.push('{ _and: [{ geo: { total_crime_incidents: { _gte: 3000 } } }, { geo: { total_crime_incidents: { _lt: 6000 } } }] }');
    if (filters.crimeIndex.includes('critical')) crimeOrConditions.push('{ geo: { total_crime_incidents: { _gte: 6000 } } }');
    if (crimeOrConditions.length > 0) {
      conditions.push(`{ _or: [${crimeOrConditions.join(', ')}] }`);
    }
  }

  // Installed solutions: store must have the install date (not null)
  if (filters.solutions && filters.solutions.length > 0) {
    const solutionFieldMap: Record<string, string> = {
      'Purchek': 'purchek_install_date',
      'Cart Containment': 'containment_install_date',
      'Cart Manager': 'cart_manager_install_date',
      'Subscription': 'subscription_start_date',
      'Paired Wheel': 'paired_wheel_install_date',
    };
    filters.solutions.forEach(sol => {
      const field = solutionFieldMap[sol];
      if (field) {
        conditions.push(`{ ${field}: { _is_null: false } }`);
      }
    });
  }

  // Whitespace solutions: store must NOT have the install date (is null)
  if (filters.whitespace && filters.whitespace.length > 0) {
    const solutionFieldMap: Record<string, string> = {
      'Purchek': 'purchek_install_date',
      'Cart Containment': 'containment_install_date',
      'Cart Manager': 'cart_manager_install_date',
      'Subscription': 'subscription_start_date',
      'Paired Wheel': 'paired_wheel_install_date',
    };
    filters.whitespace.forEach(sol => {
      const field = solutionFieldMap[sol];
      if (field) {
        conditions.push(`{ ${field}: { _is_null: true } }`);
      }
    });
  }

  if (filters.shoppingComplexOnly) {
    conditions.push(`{ nearby_penetration: { plaza_cm_customer_count: { _gt: 0 } } }`);
  }

  // Cart ordinance "No" needs _or (false OR null), can't go inside geo block
  if (filters.cartOrdinance === 'No') {
    conditions.push(`{ _or: [{ geo: { penalty_for_cart_owner: { _eq: false } } }, { geo: { penalty_for_cart_owner: { _is_null: true } } }] }`);
  }

  if (conditions.length === 0) return '';
  return `, _and: [${conditions.join(', ')}]`;
};

export const fetchStoreCountsByState = async (
  filters?: { states?: string[]; cities?: string[]; zipcodes?: string[]; brands?: string[]; retailers?: string[]; categories?: string[]; viewingMode?: string } & AdvancedFilters
): Promise<StateStoreCount[]> => {
  const filterSig = JSON.stringify(filters || {});
  const cacheKey = 'map-cache-state-counts';
  const cachedRaw = sessionStorage.getItem(cacheKey);
  if (cachedRaw) {
    try {
      const parsed = JSON.parse(cachedRaw);
      if (parsed.sig === filterSig) return parsed.data as StateStoreCount[];
    } catch { /* ignore */ }
  }

  try {
    const allStates = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR'];
    // If specific states are selected, only query those states
    const states = filters?.states && filters.states.length > 0 ? filters.states : allStates;

    // Build city filter condition if cities are selected
    const cityCondition = filters?.cities && filters.cities.length > 0
      ? `, city: { _in: [${filters.cities.map(c => `"${c.toLowerCase()}"`).join(', ')}] }`
      : '';

    // Build zipcode filter condition if zipcodes are selected
    const zipcodeCondition = filters?.zipcodes && filters.zipcodes.length > 0
      ? `, zipcode: { _in: [${filters.zipcodes.map(z => `"${z}"`).join(', ')}] }`
      : '';

    // Build brand filter condition if brands are selected
    const brandCondition = filters?.brands && filters.brands.length > 0
      ? `, brand_name: { _in: [${filters.brands.map(b => `"${b}"`).join(', ')}] }`
      : '';

    // Build retailer (ultimate_parent) filter condition via brand relationship
    const retailerCondition = filters?.retailers && filters.retailers.length > 0
      ? `, brand: { ultimate_parent: { _in: [${filters.retailers.map(r => `"${r}"`).join(', ')}] } }`
      : '';

    const channelCondition = filters?.categories && filters.categories.length > 0
      ? `, channel: { _in: [${filters.categories.flatMap(c => c.toLowerCase() === 'diy' ? [`"DIY"`, `"Diy"`] : [`"${c}"`]).join(', ')}] }`
      : '';

    const viewingModeOr = getViewingModeOrCondition(filters?.viewingMode);
    const viewingModeCondition = viewingModeOr ? `, ${viewingModeOr}` : '';

    // Build advanced filter conditions split into geo-level (inside geo:{}) and non-geo (_and at root)
    const geoCondition = buildGeoConditionsInline(filters);
    const nonGeoCondition = buildNonGeoConditionsInline(filters);

    // Build query with all state aggregations (count + avg lat/lng for centroid)
    const queryLines = states.map(state =>
      `${state}: udm_poi_aggregate(where: { geo: { state_code: { _eq: "${state}" }${cityCondition}${geoCondition} }${zipcodeCondition}${brandCondition}${retailerCondition}${channelCondition}${viewingModeCondition}${nonGeoCondition} }) { count: aggregate { count } avg: aggregate { avg { latitude longitude } } } ${state}_customers: udm_poi_aggregate(where: { geo: { state_code: { _eq: "${state}" }${cityCondition}${geoCondition} }${zipcodeCondition}${brandCondition}${retailerCondition}${channelCondition}${viewingModeCondition}${nonGeoCondition}, customer_id: { _neq: "" } }) { count: aggregate { count } }`
    );

    const query = `
      query GetStoreStateDistribution @cached {
        ${queryLines.join('\n')}
      }
    `;

    const response = await graphqlClient.request<any>(query);

    const result = states
      .map(state => ({
        state_code: state,
        count: response[state]?.count?.count || 0,
        customers: response[`${state}_customers`]?.count?.count || 0,
        centroid_lat: response[state]?.avg?.avg?.latitude ? parseFloat(response[state].avg.avg.latitude) : undefined,
        centroid_lng: response[state]?.avg?.avg?.longitude ? parseFloat(response[state].avg.avg.longitude) : undefined,
      }))
      .filter(item => item.count > 0);

    try { sessionStorage.setItem(cacheKey, JSON.stringify({ sig: filterSig, data: result })); } catch { /* ignore */ }
    return result;
  } catch (error) {
    console.error('Error fetching state counts:', error);
    return [];
  }
};

export const fetchStoresForViewport = async (
  bounds: ViewportBounds,
  filters?: { channels?: string[]; customerType?: string; states?: string[]; cities?: string[]; zipcodes?: string[]; retailers?: string[]; brands?: string[]; categories?: string[]; viewingMode?: string } & AdvancedFilters,
  detail: boolean = false
): Promise<BrandStoreForMap[]> => {
  const roundedBounds = {
    minLat: Math.round(bounds.minLat * 100) / 100,
    maxLat: Math.round(bounds.maxLat * 100) / 100,
    minLng: Math.round(bounds.minLng * 100) / 100,
    maxLng: Math.round(bounds.maxLng * 100) / 100,
  };
  const viewportSig = JSON.stringify({ ...roundedBounds, ...filters, detail });
  const viewportCacheKey = 'map-cache-viewport';
  const viewportCachedRaw = sessionStorage.getItem(viewportCacheKey);
  if (viewportCachedRaw) {
    try {
      const parsed = JSON.parse(viewportCachedRaw);
      if (parsed.sig === viewportSig) return parsed.data as BrandStoreForMap[];
    } catch { /* ignore */ }
  }

  try {
    const andConditions = [
      '{ latitude: { _gte: $minLat } }',
      '{ latitude: { _lte: $maxLat } }',
      '{ longitude: { _gte: $minLng } }',
      '{ longitude: { _lte: $maxLng } }',
      '{ closed_on: { _is_null: true } }',
    ];

    // Viewing mode filter (whitespace / recommended)
    const viewingModeOr = getViewingModeOrCondition(filters?.viewingMode);
    if (viewingModeOr) {
      andConditions.push(`{ ${viewingModeOr} }`);
    }

    if (filters?.customerType === 'Customer') {
      andConditions.push('{ customer_id: { _is_null: false } }');
    } else if (filters?.customerType === 'Non-Customer') {
      andConditions.push('{ customer_id: { _is_null: true } }');
    }

    if (filters?.channels && filters.channels.length > 0) {
      const channelList = filters.channels.map(c => `"${c}"`).join(', ');
      andConditions.push(`{ channel: { _in: [${channelList}] } }`);
    }

    if (filters?.states && filters.states.length > 0) {
      const stateList = filters.states.map(s => `"${s}"`).join(', ');
      andConditions.push(`{ geo: { state_code: { _in: [${stateList}] } } }`);
    }

    if (filters?.cities && filters.cities.length > 0) {
      const cityList = filters.cities.map(c => `"${c.toLowerCase()}"`).join(', ');
      andConditions.push(`{ geo: { city: { _in: [${cityList}] } } }`);
    }

    if (filters?.zipcodes && filters.zipcodes.length > 0) {
      const zipcodeList = filters.zipcodes.map(z => `"${z}"`).join(', ');
      andConditions.push(`{ zipcode: { _in: [${zipcodeList}] } }`);
    }

    if (filters?.retailers && filters.retailers.length > 0) {
      const retailerList = filters.retailers.map(r => `"${r}"`).join(', ');
      andConditions.push(`{ brand: { ultimate_parent: { _in: [${retailerList}] } } }`);
    }

    if (filters?.brands && filters.brands.length > 0) {
      const brandList = filters.brands.map(b => `"${b}"`).join(', ');
      andConditions.push(`{ brand_name: { _in: [${brandList}] } }`);
    }

    if (filters?.categories && filters.categories.length > 0) {
      const channelList = filters.categories.flatMap(c => c.toLowerCase() === 'diy' ? [`"DIY"`, `"Diy"`] : [`"${c}"`]).join(', ');
      andConditions.push(`{ channel: { _in: [${channelList}] } }`);
    }

    // Advanced filters
    const advancedConds = buildAdvancedConditions(filters);
    advancedConds.forEach(c => andConditions.push(`{ ${c} }`));

    const fields = detail ? POI_FIELDS : POI_FIELDS_LIGHT;

    const query = `
      query GetViewportStores($minLat: numeric!, $maxLat: numeric!, $minLng: numeric!, $maxLng: numeric!) @cached {
        udm_poi(where: { _and: [${andConditions.join(', ')}] }) {
          ${fields}
        }
      }
    `;

    const response = await graphqlClient.request<{ udm_poi: any[] }>(query, bounds);
    const result = (response.udm_poi || []).filter((p: any) => p.latitude && p.longitude).map(mapPoiToStore);
    try { sessionStorage.setItem(viewportCacheKey, JSON.stringify({ sig: viewportSig, data: result })); } catch { /* ignore */ }
    return result;
  } catch {
    return [];
  }
};

export const fetchNeighborhoodStoresForViewport = async (
  primaryStoresInViewport: BrandStoreForMap[],
  radiusMiles: number = 3,
  customerType?: 'All' | 'Customers' | 'Non-Customers',
  channels?: string[],
  retailers?: string[],
  solutions?: string[],
  whitespace?: string[]
): Promise<BrandStoreForMap[]> => {
  try {
    if (primaryStoresInViewport.length === 0) {
      return [];
    }

    const bbox = calculateCombinedBoundingBox(primaryStoresInViewport, radiusMiles);

    const query = `
      query GetViewportNeighborhoodStores(
        $minLat: numeric!
        $maxLat: numeric!
        $minLng: numeric!
        $maxLng: numeric!
      ) @cached {
        udm_poi(
          where: {
            _and: [
              { latitude: { _gte: $minLat } }
              { latitude: { _lte: $maxLat } }
              { longitude: { _gte: $minLng } }
              { longitude: { _lte: $maxLng } }
              { closed_on: { _is_null: true } }
            ]
          }
        ) {
          id
          site
          brand_name
          address
          latitude
          longitude
          channel
          customer_id
          zipcode
          purchek_install_date
          containment_install_date
          cart_manager_install_date
          subscription_start_date
          paired_wheel_install_date
          updated_store_ids(order_by: {created_at: desc}, limit: 1) {
            store_id
            created_at
          }
          nearby_penetration {
            purchek_customer_count
            penetration_purchek
            containment_customer_count
            penetration_containment
            plaza_cm_customer_count
            penetration_paired_wheel
            penetration
          }
          geo {
            city
            state_code
            crime_rate
            total_crime_incidents
            penalty_for_cart_owner
            no_of_hot_days
            no_of_cold_days
          }
          wheel {
            single_wheel_replacement_rate
            service_frequency
          }
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, {
      minLat: bbox.minLat,
      maxLat: bbox.maxLat,
      minLng: bbox.minLng,
      maxLng: bbox.maxLng
    });

    const allNearbyStores = response.udm_poi || [];
    const primaryStoreIds = new Set(primaryStoresInViewport.map(s => s.id));

    const neighborhoodStores: BrandStoreForMap[] = allNearbyStores
      .filter((store: any) => store.latitude && store.longitude && !primaryStoreIds.has(store.id))
      .map((store: any) => ({
        id: store.id,
        site: store.site,
        brand_name: store.brand_name,
        address: store.address || null,
        latitude: store.latitude,
        longitude: store.longitude,
        channel: store.channel || null,
        customer_id: store.customer_id || null,
        zipcode: store.zipcode || null,
        estimated_no_of_carts_per_store: undefined,
        purchek_install_date: store.purchek_install_date || null,
        containment_install_date: store.containment_install_date || null,
        cart_manager_install_date: store.cart_manager_install_date || null,
        subscription_start_date: store.subscription_start_date || null,
        paired_wheel_install_date: store.paired_wheel_install_date || null,
        nearby_penetration: store.nearby_penetration || null,
        geo: store.geo || null,
        wheel: store.wheel || null,
        updated_store_ids: store.updated_store_ids || null
      }))
      .filter((storeToCheck: BrandStoreForMap) => {
        return primaryStoresInViewport.some(primaryStore => {
          const distance = calculateDistance(
            primaryStore.latitude,
            primaryStore.longitude,
            storeToCheck.latitude,
            storeToCheck.longitude
          );
          return distance <= radiusMiles;
        });
      });

    const uniqueNeighborhoods = new Map<string, BrandStoreForMap>();
    neighborhoodStores.forEach(store => {
      if (!uniqueNeighborhoods.has(store.id)) {
        uniqueNeighborhoods.set(store.id, store);
      }
    });

    let filteredStores = Array.from(uniqueNeighborhoods.values());

    if (customerType && customerType !== 'All') {
      filteredStores = filteredStores.filter(store => {
        const isCustomer = store.customer_id != null && store.customer_id !== '';

        if (customerType === 'Customers') {
          return isCustomer;
        } else if (customerType === 'Non-Customers') {
          return !isCustomer;
        }
        return true;
      });
    }

    if (channels && channels.length > 0) {
      filteredStores = filteredStores.filter(store =>
        channels.includes(store.channel || '')
      );
    }

    if (retailers && retailers.length > 0) {
      filteredStores = filteredStores.filter(store =>
        retailers.includes(store.brand_name || '')
      );
    }

    // Solutions filter: keep stores that have at least one selected solution installed
    if (solutions && solutions.length > 0) {
      const solField: Record<string, keyof BrandStoreForMap> = {
        Purchek: 'purchek_install_date',
        'Cart Containment': 'containment_install_date',
        'Cart Manager': 'cart_manager_install_date',
        Subscription: 'subscription_start_date',
        'Paired Wheel': 'paired_wheel_install_date',
      };
      filteredStores = filteredStores.filter(store =>
        solutions.some(sol => !!store[solField[sol]])
      );
    }

    // Whitespace filter: keep stores missing at least one selected solution
    if (whitespace && whitespace.length > 0) {
      const wsField: Record<string, keyof BrandStoreForMap> = {
        Purchek: 'purchek_install_date',
        'Cart Containment': 'containment_install_date',
        'Cart Manager': 'cart_manager_install_date',
        Subscription: 'subscription_start_date',
        'Paired Wheel': 'paired_wheel_install_date',
      };
      filteredStores = filteredStores.filter(store =>
        whitespace.some(ws => !store[wsField[ws]])
      );
    }

    return filteredStores;
  } catch (error: any) {
    return [];
  }
};
